/**
 * Shared appearance switching — ui-core's `useAppearance()` plus the site's
 * transition suppression, with the tri-state mirrored into a Vue ref.
 *
 * `useAppearance` suppresses light-DOM transitions across the flip itself, but
 * not shadow-scoped ones (deliberate — see main.css), so `e-button`'s base part
 * cross-fades while the page around it has already repainted. Holding
 * `appearance-switching` on `<html>` over the flip lets the `::part` rule in
 * main.css sit that frame out; the rAF pair clears it after the repaint,
 * leaving hover transitions untouched.
 *
 * WHY `mode` IS MIRRORED. The library's `isDark` and `mode` are refs from its
 * OWN bundled reactivity, not Vue's. Vue unwraps only its own refs, so reading
 * `mode` in a template yields the ref OBJECT — `mode === "dark"` is never true
 * and a template that keys off it silently renders nothing. (The same reason
 * `AppearanceToggle` has always read the `.dark` class through `useIsDark`
 * rather than the library's `isDark`.)
 *
 * So the tri-state is mirrored into a module-level Vue ref, seeded from the
 * library and updated on every write. Module-level rather than per-caller
 * because two controls read it — the toggle button and the account menu's
 * Appearance submenu — and per-instance refs would drift apart the moment one
 * of them changed the mode.
 *
 * This mirrors, it does not own: `setMode` and `toggle` still go through the
 * library, which remains the only writer of the class and the storage key.
 */
import { useAppearance } from "@econnect/ui-core";

/**
 * The library models this tri-state but re-exports only `useAppearance` and
 * `configureAppearance` from its root, so the union is restated here rather
 * than imported from a deep `dist/shared/…` path that is not public API.
 */
export type AppearanceMode = "light" | "dark" | "auto";

function suppressTransitions(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.add("appearance-switching");
  requestAnimationFrame(() =>
    requestAnimationFrame(() => root.classList.remove("appearance-switching")),
  );
}

// One mirror for every caller, like `useIsDark`'s shared ref.
let sharedMode: Ref<AppearanceMode> | null = null;

export function useAppearanceSwitch() {
  const appearance = useAppearance({ valueLight: "" });
  // Resolved dark/light still comes from the `.dark` class via `useIsDark`,
  // which IS Vue-reactive; `mode` is the persisted choice, which `isDark`
  // cannot express (it cannot tell `auto` from the value it resolved to).
  const isDark = useIsDark();

  const readMode = (): AppearanceMode =>
    (appearance.mode?.value as AppearanceMode) ?? "auto";

  if (!sharedMode) sharedMode = ref<AppearanceMode>(readMode());
  const mode = sharedMode;

  // The library reads storage on first use, which may land after our ref was
  // created — re-seed once mounted so a persisted choice shows its check.
  onMounted(() => {
    mode.value = readMode();
  });

  function sync(): void {
    mode.value = readMode();
  }

  function toggle(): void {
    suppressTransitions();
    appearance.toggle();
    sync();
  }

  function setMode(next: AppearanceMode): void {
    suppressTransitions();
    appearance.setMode(next);
    sync();
  }

  return { isDark, mode, toggle, setMode };
}
